import { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AudioStatusResponse {
  success: boolean;
  audio: {
    status: 'pending' | 'processing' | 'ready' | 'failed';
    progress?: number;
    estimatedSeconds?: number;
    audioUrl?: string;
    durationSeconds?: number;
    errorMessage?: string;
  };
}

interface StatusPollerProps {
  queueItemId: string;
  apiUrl: string;
  initialProgress: number;
  estimatedSeconds?: number;
  onStatusUpdate: (status: AudioStatusResponse) => void;
}

// ============================================================================
// Brand Icons (SVG)
// ============================================================================

const IconMicrophone = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#0ea5e9' }}>
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

// ============================================================================
// Status Poller Component
// ============================================================================

export function StatusPoller({
  queueItemId,
  apiUrl,
  initialProgress,
  estimatedSeconds = 45,
  onStatusUpdate,
}: StatusPollerProps) {
  const [progress, setProgress] = useState(initialProgress);
  const [eta, setEta] = useState(estimatedSeconds);
  const intervalRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef(Date.now());
  const onStatusUpdateRef = useRef(onStatusUpdate);
  
  // Keep the callback ref up to date without triggering effect re-runs
  useEffect(() => {
    onStatusUpdateRef.current = onStatusUpdate;
  }, [onStatusUpdate]);

  const pollStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiUrl}/v1/audio/queue/${queueItemId}/status`
      );
      const data: AudioStatusResponse = await response.json();

      if (data.success) {
        // Update progress
        if (data.audio.progress !== undefined) {
          setProgress(data.audio.progress);
        }

        if (data.audio.estimatedSeconds !== undefined) {
          setEta(data.audio.estimatedSeconds);
        }

        // Check if ready or failed
        if (data.audio.status === 'ready' || data.audio.status === 'failed') {
          // Stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          onStatusUpdateRef.current(data);
        }
      }
    } catch (error) {
      console.error('Status poll error:', error);
    }
  }, [apiUrl, queueItemId]);

  useEffect(() => {
    // Poll immediately on mount
    pollStatus();
    
    // Start polling every 2 seconds
    intervalRef.current = window.setInterval(pollStatus, 2000);

    // Simulate progress if we don't get updates
    progressIntervalRef.current = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const expectedProgress = Math.min(95, (elapsed / estimatedSeconds) * 100);
        return Math.max(prev, expectedProgress);
      });
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [pollStatus, estimatedSeconds]);

  return (
    <div className="status-container">
      <div className="spinner" />
      <p className="status-text">
        <IconMicrophone />
        Generating audio...
      </p>
      <p className="status-subtext">
        {eta > 0
          ? `Usually takes ${Math.ceil(eta)} seconds`
          : 'Almost ready...'}
      </p>

      <div className="progress-bar-container">
        <div className="progress-bar-bg">
          <div
            className="progress-bar-fill"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
